import Swiper from 'swiper';
import { Navigation, A11y } from 'swiper/modules';
import FormUI from './formUI';

import { loadCaptcha } from 'root/core/tracking/recaptcha/recaptcha';
import { initPhoneLib } from 'root/main/components/forms/phoneInit';
import { LoggingHandler } from 'root/utils/errorReporting/LoggingHandler';
import { handleDomainPrefixField, handleZipCodeField } from 'root/helpers/formHelpers';
import { progressIndicator } from 'root/main/components/progressIndicator';

Swiper.use([Navigation, A11y]);

export default class FormUISlider extends FormUI {
    constructor(el) {
        super(el);
        this.slider = this.form.querySelector('[data-form-slider]');
        this.nextButtonEl = this.form.querySelector('[data-form-slider-next-button]');
        this.prevButtonEl = this.form.querySelector('.js-back-btn');
        this.firstPanelDiv = this.form.querySelector('[data-form-panel="1"]');
        this.secondPanelDiv = this.form.querySelector('[data-form-panel="2"]');
        this.balanceTwoStepFormPanels();
        this.firstPanelInputs = Array.from(
            this.firstPanelDiv?.querySelectorAll(this.inputSelector)
        );
        this.timelineContainer = this.form.querySelector(
            '[data-leadform-step=progress-indicator]'
        );
    }

    fieldsArrayWithExceptionsExcluded(fields) {
        fields = fields ?? [];
        const exceptionArray = ['general_opt_in'];
        return Array.from(fields).filter(
            field => !exceptionArray.includes(field.firstElementChild.name)
        );
    }

    balanceTwoStepFormPanels() {
        const allFormFields = Array.from(
            this.form.querySelectorAll(
                '[data-leadform-steps="main-fields-wrapper"] [data-formfield=ls-form-field]'
            )
        );
        const adjustedFormFieldsCount = allFormFields.filter(field => {
            return !['agnostic_business_type', 'postal_code'].includes(
                field.querySelector('input,select').name
            );
        }).length;
        let adjustedMappedFormFieldsIndexModifier = 0;
        const assignedFormFields = allFormFields.map((field, index) => {
            if (
                ['agnostic_business_type', 'postal_code'].includes(
                    field.querySelector('input,select').name
                )
            ) {
                adjustedMappedFormFieldsIndexModifier++;
            }
            return {
                field: field,
                step:
                    index - adjustedMappedFormFieldsIndexModifier <
                    adjustedFormFieldsCount / 2
                        ? 'first_step'
                        : 'second_step',
            };
        });
        const firstStepMainFieldsWrapper = this.firstPanelDiv?.querySelector(
            '[data-leadform-steps="main-fields-wrapper"]'
        );
        const secondStepMainFieldsWrapper = this.secondPanelDiv?.querySelector(
            '[data-leadform-steps="main-fields-wrapper"]'
        );
        if (firstStepMainFieldsWrapper && secondStepMainFieldsWrapper) {
            assignedFormFields.forEach(({ field, step }) => {
                if (step === 'first_step') {
                    firstStepMainFieldsWrapper.appendChild(field);
                } else {
                    secondStepMainFieldsWrapper.appendChild(field);
                }
            });
        }
        this.firstPanelDiv?.classList.remove('tw:hidden');
        this.secondPanelDiv?.classList.remove('tw:hidden');
    }

    isFirstSlideValid() {
        const firstStepFields = this.getPanelFields(this.firstPanelDiv);

        return this.fieldsArrayWithExceptionsExcluded(firstStepFields).every(field => {
            if (field.firstElementChild.type === 'checkbox') {
                if (field.firstElementChild.required) {
                    return field.firstElementChild.checked;
                } else {
                    return true;
                }
            }
            return field.querySelector(this.inputSelector)?.checkValidity();
        });
    }

    advanceToNextSlide(e, swiper) {
        const secondStepFields = this.getPanelFields(this.secondPanelDiv);

        this.formValidationOnSubmit(e, this.firstPanelInputs);
        if (this.isFirstSlideValid()) {
            swiper.slideNext();

            if (this.timelineContainer) {
                progressIndicator(
                    this.timelineContainer,
                    1
                ).syncProgressIndicatorToStep();
            }

            secondStepFields[0]?.focus();
        }
    }

    reverseToPreviousSlide(swiper) {
        const firstStepFields = this.getPanelFields(this.firstPanelDiv);

        swiper.slidePrev();
        if (this.timelineContainer) {
            progressIndicator(this.timelineContainer, 0).syncProgressIndicatorToStep();
        }
        firstStepFields[0]?.focus();
    }

    makeInactiveSlidesInert(swiper) {
        const allSlides = swiper.slides;
        allSlides.forEach(slide => {
            const slideIsActive = slide.classList.contains('swiper-slide-active');
            if (slideIsActive) {
                slide.removeAttribute('inert');
            } else {
                slide.setAttribute('inert', '');
            }
        });
    }

    getPanelFields(panel) {
        return panel?.querySelectorAll('[data-formfield=ls-form-field]');
    }

    initSwiper() {
        new Swiper(this.slider, {
            slidesPerView: 1,
            centeredSlides: true,
            simulateTouch: false,
            spaceBetween: 24,
            a11y: {
                enabled: true,
                scrollOnFocus: false,
            },
            on: {
                init: swiper => {
                    if (this.nextButtonEl) {
                        this.nextButtonEl.addEventListener('click', e => {
                            this.advanceToNextSlide(e, swiper);
                        });
                        this.nextButtonEl.addEventListener('keydown', e => {
                            if (
                                e.code === 'Tab' &&
                                !e.shiftKey &&
                                this.isFirstSlideValid()
                            ) {
                                this.advanceToNextSlide(e, swiper);
                            }
                        });
                    }

                    if (this.prevButtonEl) {
                        this.prevButtonEl.addEventListener('click', () => {
                            this.reverseToPreviousSlide(swiper);
                        });
                    }
                    const secondStepFields = this.getPanelFields(this.secondPanelDiv);
                    if (secondStepFields[0]) {
                        secondStepFields[0].addEventListener('keydown', e => {
                            if (e.code === 'Tab' && e.shiftKey) {
                                this.reverseToPreviousSlide(swiper);
                            }
                        });
                    }

                    this.makeInactiveSlidesInert(swiper);
                },
                slideChangeTransitionEnd: swiper => {
                    this.makeInactiveSlidesInert(swiper);
                },
            },
        });
    }

    loadFormSpecs() {
        if (this.slider) {
            this.balanceTwoStepFormPanels();
            this.inputs = Array.prototype.slice.call(
                this.form.querySelectorAll(this.inputSelector)
            );
            if (this.recaptchaContainer) {
                loadCaptcha(this.form, 'formload');
            } else {
                this.form.classList.remove(this.hiddenFormClass);
            }
            this.initFieldEvents(this.inputs);
            this.autofillBusinessTypeWithCookieValue();
            this.autofillFormsWithLocalStorageData(this.inputs); // this should always be called BEFORE the phoneLibInit function
            /*
             * initPhoneLib() should always be called AFTER the autofill function. Because it affects the way that the
             * country gets pre-selected in the phone dropdown. If initPhoneLib() gets called before the autofill, the
             * selected country will be: either the country that the user is at OR the first country in the dropdown.
             * However, those 2 options might not correspond with the phone number saved in the local storage.
             * */
            try {
                [this.phoneLibInstance, this.phoneField] = initPhoneLib(this.form);
            } catch (e) {
                initPhoneLib(this.form).destroy();
                LoggingHandler().logError(new Error(e), {
                    searchTerm: 'phone-library-init-error',
                    tags: ['forms', 'ui'],
                    fileName: 'formUI.js',
                    additionalData: {
                        url: window.location.href,
                    },
                });
            }
            this.raisePreFilledInputLabels();
            this.handleGooglePlacesFieldAutocomplete(
                'address',
                this.setDataAttributeForAddress
            );
            this.handleGooglePlacesFieldAutocomplete(
                'zip',
                this.setDataAttributeForZipCode
            );
            handleDomainPrefixField({
                form: this.form,
            }).toggleDisplay();
            handleZipCodeField(this.form);

            this.initSwiper();
        }
    }
}
